// Dispatch due-date rule for the admin orders "needs to go today" filter.
//
// Mirrors the BE's KPI_DUE_DAY_CTES (dashboard.controller.js) exactly so
// the clock filter and the Team dashboard's On Time Shipment Rate agree
// on which day an order must ship (per Alex, 2026-08-25):
//
//   • placed before 13:00 London on a working day → due that day;
//     placed at/after 13:00 → next working day
//   • Saturday / Sunday and UK bank holidays are not dispatch days →
//     roll to the next working day
//   • STANDARD orders due on the first working day AFTER a closed day
//     slip one more working day — the old "due Monday → Tuesday" rule,
//     generalised so a bank-holiday Monday hands its logic to Tuesday
//     (per Alex, 2026-08-31). Express (express + priority) keeps the
//     first working day.
//   • time spent ON HOLD does not count: "placed" is the later of the
//     placed time and the order's last release from hold (dueClockStart
//     below; per Alex, 2026-10-02). An order still on hold is not due.
//
// All wall-clock maths is done on the Europe/London clock regardless of
// the browser's zone — the warehouse's cut-off is a London 13:00.

export const DISPATCH_CUTOFF_HOUR = 13;
const LONDON = "Europe/London";

// ISO date (YYYY-MM-DD) + hour of an instant on the London wall clock.
const londonParts = (ms: number): { date: string; hour: number } => {
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: LONDON,
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", hourCycle: "h23",
    }).formatToParts(new Date(ms));
    const get = (type: string) => parts.find(p => p.type === type)?.value ?? "";
    return {
        date: `${get("year")}-${get("month")}-${get("day")}`,
        // `hourCycle: "h23"` still yields "24" for midnight in some engines.
        hour: Number(get("hour")) % 24,
    };
};

export const londonDateOf = (ms: number): string => londonParts(ms).date;

const addDays = (isoDate: string, n: number): string => {
    const d = new Date(`${isoDate}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
};

// ISO weekday: 1 = Monday … 7 = Sunday (Postgres ISODOW).
const isoDow = (isoDate: string): number => {
    const d = new Date(`${isoDate}T00:00:00Z`).getUTCDay();
    return d === 0 ? 7 : d;
};

// UK bank holidays — MUST match the BE's `bank_holidays` seed (country
// 'UK', gov.uk/bank-holidays.json). Extend both together each year.
const UK_BANK_HOLIDAYS = new Set([
    "2026-01-01", "2026-04-03", "2026-04-06", "2026-05-04", "2026-05-25",
    "2026-08-31", "2026-12-25", "2026-12-28",
    "2027-01-01", "2027-03-26", "2027-03-29", "2027-05-03", "2027-05-31",
    "2027-08-30", "2027-12-27", "2027-12-28",
]);

// Not a dispatch day: weekend or UK bank holiday.
const isClosedDay = (isoDate: string): boolean =>
    isoDow(isoDate) >= 6 || UK_BANK_HOLIDAYS.has(isoDate);

const nextWorkingDay = (isoDate: string): string => {
    let d = isoDate;
    while (isClosedDay(d)) d = addDays(d, 1);
    return d;
};

export const isExpressDeliveryType = (deliveryType: string | null | undefined): boolean => {
    const dt = (deliveryType || "").toLowerCase();
    return dt === "express" || dt === "priority";
};

// Where an order's due clock starts: when it was placed, or when it last
// came off hold if that is later (BE dispatch-due.js dueClockStartSql).
// Pass the result as `placedIso` below.
export const dueClockStart = (
    placedIso: string | null | undefined,
    holdReleasedIso: string | null | undefined,
): string | null | undefined => {
    const released = holdReleasedIso ? Date.parse(holdReleasedIso) : NaN;
    if (Number.isNaN(released)) return placedIso;
    const placed = placedIso ? Date.parse(placedIso) : NaN;
    return Number.isNaN(placed) || released > placed ? holdReleasedIso : placedIso;
};

// The London date an order must ship by. `placedIso` is when the order
// was placed on the channel (or released from hold — dueClockStart).
// Returns null when the placed time is unparseable (never happens for
// BE rows, but the FE type is loose).
export const dispatchDueDate = (placedIso: string | null | undefined, express: boolean): string | null => {
    const ms = placedIso ? Date.parse(placedIso) : NaN;
    if (Number.isNaN(ms)) return null;
    const { date, hour } = londonParts(ms);
    const day0 = hour >= DISPATCH_CUTOFF_HOUR ? addDays(date, 1) : date;
    const day1 = nextWorkingDay(day0);
    return !express && isClosedDay(addDays(day1, -1)) ? nextWorkingDay(addDays(day1, 1)) : day1;
};

// Due today or already overdue, as of `nowMs`.
export const isDueByToday = (placedIso: string | null | undefined, express: boolean, nowMs: number): boolean => {
    const due = dispatchDueDate(placedIso, express);
    return due !== null && due <= londonDateOf(nowMs);
};

// Past today's order cut-off on the London clock. Gates the Pick Queue /
// Shelf Drop "must ship today" toggle: before the cut-off everything in
// the queue is still today's work, so the filter has nothing to
// separate. `cutoff` is today's standard cut-off for the warehouse as
// "HH:MM" from GET /dispatch-admin/channel-rules/today (Settings ›
// Dispatch › Channel cut-offs, 2026-10-13): null = no cut-off today (a
// weekend row) so the toggle never shows; omitted = the old 13:00.
export const isAfterDispatchCutoff = (nowMs: number, cutoff?: string | null): boolean => {
    if (cutoff === null) return false;
    const { hour, minute } = londonHourMinute(nowMs);
    if (cutoff === undefined) return hour >= DISPATCH_CUTOFF_HOUR;
    const m = /^(\d{2}):(\d{2})/.exec(cutoff);
    if (!m) return hour >= DISPATCH_CUTOFF_HOUR;
    return hour * 60 + minute >= Number(m[1]) * 60 + Number(m[2]);
};

const londonHourMinute = (ms: number): { hour: number; minute: number } => {
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: LONDON, hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(new Date(ms));
    const get = (type: string) => parts.find(p => p.type === type)?.value ?? "0";
    return { hour: Number(get("hour")) % 24, minute: Number(get("minute")) };
};
